"use client";

import React, { useState, useRef, useEffect, memo, useCallback } from "react";
import { Check, X } from "lucide-react";

interface EditableTitleProps {
  title: string;
  onSave: (newTitle: string) => void;
  className?: string;
  placeholder?: string;
}

const EditableTitle: React.FC<EditableTitleProps> = memo(({
  title,
  onSave,
  className = "",
  placeholder = "Enter title...",
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const editableRef = useRef<HTMLDivElement>(null);

  const handleSave = useCallback(async () => {
    if (editableRef.current) {
      const newTitle = editableRef.current.textContent?.trim() || "";
      if (newTitle && newTitle !== title) {
        await onSave(newTitle);
      }
    }
    setIsEditing(false);
  }, [title, onSave]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isEditing &&
        editableRef.current &&
        !editableRef.current.contains(event.target as Node)
      ) {
        handleSave();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isEditing, handleSave]);


  useEffect(() => {
    if (isEditing && editableRef.current) {
      editableRef.current.focus();
      // Move cursor to end
      const range = document.createRange();
      const sel = window.getSelection();
      range.selectNodeContents(editableRef.current);
      range.collapse(false);
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
  }, [isEditing]);


  const handleCancel = () => {
    if (editableRef.current) {
      editableRef.current.textContent = title;
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      e.preventDefault();
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <div className="flex items-start gap-2">
        <div
          ref={editableRef}
          contentEditable
          onKeyDown={handleKeyDown}
          suppressContentEditableWarning
          className="text-sm sm:text-xl font-medium sm:font-semibold text-gray-900 outline-none break-words focus:outline-none min-w-[50px]"
          style={{ whiteSpace: "wrap" }}
        >
          {title}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={handleSave}
            className="p-1.5 rounded-md hover:bg-gray-100 border border-gray-100 transition-colors"
            title="Save"
          >
            <Check className="w-4 h-4 text-green-600" />
          </button>
          <button
            onClick={handleCancel}
            className="p-1.5 rounded-md hover:bg-gray-100 transition-colors border border-gray-100"
            title="Cancel"
          >
            <X className="w-4 h-4 text-red-600" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-2 group ${className}`}>
      <h1
        onClick={() => setIsEditing(true)}
        className="text-sm sm:text-xl font-medium sm:font-semibold text-gray-900 cursor-pointer hover:opacity-70 transition-opacity"
      >
        {title}
      </h1>
    </div>
  );
});

EditableTitle.displayName = "EditableTitle";

export default EditableTitle;